import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/customer/Header';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import ConfirmModal from '../../components/common/ConfirmModal';
import api from '../../utils/api';
import { getImageUrl } from '../../utils/imageUrl';
import { useTranslation } from 'react-i18next';
import './Checkout.css';

const Checkout = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  
  const cartContext = useCart();
  const cart = cartContext.cart || cartContext.cartItems || [];
  const getCartTotal = cartContext.getCartTotal || cartContext.getTotalPrice || (() => 0);
  const clearCart = cartContext.clearCart || cartContext.clear || (() => {});
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentMethods, setPaymentMethods] = useState({
    bog_ipay: { enabled: false, settings: {} },
    bog_transfer: { enabled: false, settings: {} },
    tbc_transfer: { enabled: false, settings: {} }
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'alert',
    title: '',
    message: '',
    onConfirm: null
  });
  
  const [formData, setFormData] = useState({
    firstName: user?.name?.split(' ')[0] || '',
    lastName: user?.name?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: 'Tbilisi',
    postalCode: '',
    country: 'Georgia',
    paymentMethod: '',
    notes: ''
  });

  const shippingCost = 5;
  const taxRate = 0.18;
  const subtotal = getCartTotal();
  const tax = subtotal * taxRate;
  const total = subtotal;

  const showModal = (type, title, message, onConfirm = null) => {
    setModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm
    });
  };

  const closeModal = () => {
    setModal({ ...modal, isOpen: false });
  };

  useEffect(() => {
    fetchPaymentSettings();
  }, []);

  useEffect(() => {
    if (initialLoad) {
      setInitialLoad(false);
      return;
    }
    
    if (!cart || cart.length === 0) {
      navigate('/cart');
    }
  }, [cart, navigate, initialLoad]);

  const fetchPaymentSettings = async () => {
    try {
      const response = await api.get('/payments/settings');
      
      if (response.data.success) {
        const settings = {
          bog_ipay: { enabled: false, settings: {} },
          bog_transfer: { enabled: false, settings: {} },
          tbc_transfer: { enabled: false, settings: {} }
        };
        
        response.data.data.forEach(method => {
          if (method.payment_method === 'bank_transfer') {
            settings.bog_transfer = {
              enabled: method.enabled,
              settings: method.settings
            };
            settings.tbc_transfer = {
              enabled: method.enabled,
              settings: method.settings
            };
          } else {
            settings[method.payment_method] = {
              enabled: method.enabled,
              settings: method.settings
            };
          }
        });
        
        setPaymentMethods(settings);
        
        if (settings.bog_ipay?.enabled) {
          setFormData(prev => ({ ...prev, paymentMethod: 'bog_ipay' }));
        } else if (settings.bog_transfer?.enabled) {
          setFormData(prev => ({ ...prev, paymentMethod: 'bog_transfer' }));
        } else if (settings.tbc_transfer?.enabled) {
          setFormData(prev => ({ ...prev, paymentMethod: 'tbc_transfer' }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch payment settings:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    if (e.target.name === 'paymentMethod') {
      setReceiptFile(null);
      setReceiptPreview(null);
    }
  };

  // ✅ FIXED: Only for bank transfer (creates order immediately)
  const createOrder = async () => {
    try {
      const orderData = {
        customer_name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        shipping_address: `${formData.address}, ${formData.city}, ${formData.country}`,
        postal_code: formData.postalCode,
        items: cart.map(item => ({
          product_id: item.id,
          quantity: item.quantity,
          price: item.price
        })),
        subtotal: subtotal,
        shipping_cost: shippingCost,
        tax: tax,
        total: total,
        payment_method: formData.paymentMethod,
        notes: formData.notes,
        status: 'pending'
      };

      const response = await api.post('/orders', orderData);
      
      if (response.data.success) {
        return response.data.order;
      } else {
        throw new Error('Failed to create order');
      }
    } catch (error) {
      console.error('Create order error:', error);
      throw error;
    }
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        showModal('error', 'Invalid File', 'Please upload only JPG, PNG, or WebP images');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        showModal('error', 'File Too Large', 'Please upload an image smaller than 5MB');
        return;
      }
      
      setReceiptFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleManualTransferSubmit = async () => {
    if (!receiptFile) {
      showModal('error', 'Receipt Required', 'Please upload your payment receipt before submitting');
      return;
    }
    
    setUploadingReceipt(true);
    
    try {
      const order = await createOrder();
      const orderId = order.id;
      
      const formDataUpload = new FormData();
      formDataUpload.append('receipt', receiptFile);
      formDataUpload.append('order_id', orderId);
      
      const uploadResponse = await api.post('/orders/upload-receipt', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (uploadResponse.data.success) {
        showModal(
          'success',
          'Order Submitted',
          `Order #${orderId} submitted successfully! We'll confirm your payment within 24 hours.`,
          () => {
            clearCart();
            navigate(`/payment-success?order=${orderId}`);
          }
        );
      }
    } catch (error) {
      console.error('Receipt upload error:', error);
      showModal('error', 'Upload Failed', error.response?.data?.error || 'Failed to upload receipt');
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // ✅ BOG iPay - DON'T create order yet! Store data for callback
      if (formData.paymentMethod === 'bog_ipay') {
  // Prepare order data
  const orderData = {
    customer_name: `${formData.firstName} ${formData.lastName}`,
    email: formData.email,
    phone: formData.phone,
    shipping_address: `${formData.address}, ${formData.city}, ${formData.country}`,
    postal_code: formData.postalCode,
    items: cart.map(item => ({
      product_id: item.id,
      quantity: item.quantity,
      price: item.price,
      name: item.name
    })),
    subtotal: subtotal,
    shipping_cost: shippingCost,
    tax: tax,
    total: total,
    notes: formData.notes
  };
  
  // ✅ Store in localStorage (will retrieve on success page)
  localStorage.setItem('pending_order', JSON.stringify(orderData));
  
  const items = cart.map(item => ({
    product_id: item.id.toString(),
    quantity: item.quantity,
    unit_price: parseFloat(item.price).toFixed(2),
    product_name: item.name
  }));

  // Send payment request (no order_data needed!)
  const response = await api.post('/payments/create-payment', {
    amount: total,
    currency: 'GEL',
    description: `Order - ${cart.length} item(s)`,
    items: items
  });

  if (response.data.success) {
    // ✅ Store BOG order ID
    localStorage.setItem('bog_order_id', response.data.bogOrderId);
    clearCart();
    window.location.href = response.data.paymentUrl;
  } else {
    throw new Error('Failed to create payment');
  }
}
      // Bank transfer - keep old flow (creates order immediately)
      else if (formData.paymentMethod === 'bog_transfer' || formData.paymentMethod === 'tbc_transfer') {
        showModal(
          'alert',
          'Upload Receipt',
          'Please upload your payment receipt below to complete the order.'
        );
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.error || 'Failed to process order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!cart || cart.length === 0) {
    return (
      <div className="checkout-page" style={{ paddingTop: '5rem' }}>
        <div className="checkout-container">
          <div style={{ 
            textAlign: 'center', 
            padding: '4rem 2rem',
            background: 'white',
            borderRadius: 'var(--radius-xl)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
          }}>
            <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>🛒 Your cart is empty</h2>
            <p style={{ color: 'var(--gray)', marginBottom: '2rem' }}>
              Add some items to your cart before checking out
            </p>
            <Button onClick={() => navigate('/shop')}>
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page" style={{ paddingTop: '4rem' }}>      
      <div className="checkout-container">
        <div className="checkout-header">
          <h1>{t('checkout.title')}</h1>
          <p>{t('checkout.subtitle')}</p>
        </div>
        
        <div className="checkout-content">
          <form className="checkout-form" onSubmit={handleSubmit}>
            {/* Contact Information */}
            <div className="checkout-section">
              <h2 className="section-title">
                <span className="section-icon">👤</span>
                {t('checkout.contactInfo')}
              </h2>
              <div className="form-grid">
                <div className="form-group">
                  <Input
                    label={t('checkout.firstName')}
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="გიორგი"
                    required
                  />
                </div>
                <div className="form-group">
                  <Input
                    label={t('checkout.lastName')}
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="ბერიძე"
                    required
                  />
                </div>
                <div className="form-group">
                  <Input
                    label={t('checkout.email')}
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="giorgi@example.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <Input
                    label={t('checkout.phone')}
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+995 555 123 456"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="checkout-section">
              <h2 className="section-title">
                <span className="section-icon">📍</span>
                {t('checkout.address')}
              </h2>
              <div className="form-grid">
                <div className="form-group full-width">
                  <Input
                    label={t('checkout.address')}
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="რუსთაველის გამზ. 123, ბინა 45"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    {t('checkout.city')} <span className="required">*</span>
                  </label>
                  <select
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="form-select"
                    required
                  >
                    <option value="Tbilisi">Tbilisi (თბილისი)</option>
                    <option value="Batumi">Batumi (ბათუმი)</option>
                    <option value="Kutaisi">Kutaisi (ქუთაისი)</option>
                    <option value="Rustavi">Rustavi (რუსთავი)</option>
                    <option value="Gori">Gori (გორი)</option>
                    <option value="Zugdidi">Zugdidi (ზუგდიდი)</option>
                    <option value="Poti">Poti (ფოთი)</option>
                    <option value="Telavi">Telavi (თელავი)</option>
                    <option value="Other">სხვა</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="checkout-section">
              <h2 className="section-title">
                <span className="section-icon">💳</span>
                {t('checkout.paymentMethod')}
              </h2>
              <div className="payment-methods">
                {paymentMethods.bog_ipay?.enabled && (
                  <label className={`payment-option ${formData.paymentMethod === 'bog_ipay' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="bog_ipay"
                      checked={formData.paymentMethod === 'bog_ipay'}
                      onChange={handleChange}
                    />
                    <div className="payment-option-content">
                      <span className="payment-icon">💳</span>
                      <div className="payment-option-text">
                        <strong>BOG iPay</strong>
                        <p>Credit/Debit Card - Visa, Mastercard</p>
                      </div>
                      <span className="payment-badge">Secure</span>
                    </div>
                  </label>
                )}

                {paymentMethods.bog_transfer?.enabled && (
                  <label className={`payment-option ${formData.paymentMethod === 'bog_transfer' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="bog_transfer"
                      checked={formData.paymentMethod === 'bog_transfer'}
                      onChange={handleChange}
                    />
                    <div className="payment-option-content">
                      <span className="payment-icon">🏦</span>
                      <div className="payment-option-text">
                        <strong>BOG Bank Transfer</strong>
                        <p>Transfer to Bank of Georgia account</p>
                      </div>
                    </div>
                  </label>
                )}

                {paymentMethods.tbc_transfer?.enabled && (
                  <label className={`payment-option ${formData.paymentMethod === 'tbc_transfer' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="tbc_transfer"
                      checked={formData.paymentMethod === 'tbc_transfer'}
                      onChange={handleChange}
                    />
                    <div className="payment-option-content">
                      <span className="payment-icon">🏦</span>
                      <div className="payment-option-text">
                        <strong>TBC Bank Transfer</strong>
                        <p>Transfer to TBC Bank account</p>
                      </div>
                    </div>
                  </label>
                )}
              </div>

              {/* BOG iPay Info */}
              {formData.paymentMethod === 'bog_ipay' && paymentMethods.bog_ipay?.enabled && (
                <div className="payment-info">
                  <div className="supported-cards">
                    <p>Supported cards:</p>
                    <div className="card-logos">
                      <img 
                        src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" 
                        alt="Visa" 
                        className="card-logo"
                      />
                      <img 
                        src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" 
                        alt="Mastercard" 
                        className="card-logo"
                      />
                      <span className="card-text">BOG Cards</span>
                    </div>
                  </div>
                </div>
              )}

              {/* BOG Bank Transfer Details */}
              {formData.paymentMethod === 'bog_transfer' && paymentMethods.bog_transfer?.enabled && (
                <div className="bank-transfer-section">
                  <div className="bank-details-card">
                    <h3 className="bank-transfer-title">🏦 საქართველოს ბანკი</h3>
                    
                    <div className="bank-detail-row">
                      <span>ანგარიში (IBAN):</span>
                      <strong>{paymentMethods.bog_transfer.settings.iban || 'GE00BG0000000000000000'}</strong>
                    </div>
                    <div className="bank-detail-row">
                      <span>თანხა:</span>
                      <strong>{total.toFixed(2)} ₾</strong>
                    </div>
                    <div className="bank-detail-row">
                      <span>მიმღები:</span>
                      <strong>{paymentMethods.bog_transfer.settings.account_holder || 'CLOTH Store'}</strong>
                    </div>
                    <div className="bank-detail-row">
                      <span>დანიშნულება:</span>
                      <strong>შეკვეთა</strong>
                    </div>
                    
                    <div className="receipt-upload-section">
                      <h4 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
                        📸 ატვირთეთ ქვითარი
                      </h4>
                      <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
                        გადახდის შემდეგ ატვირთეთ თქვენი გადახდის ქვითარი, რათა დავადასტუროთ თქვენი შეკვეთა.
                      </p>
                      
                      <div className="receipt-upload-container">
                        <input
                          type="file"
                          id="receipt-upload"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={handleReceiptChange}
                          style={{ display: 'none' }}
                        />
                        
                        {receiptPreview ? (
                          <div className="receipt-preview">
                            <img src={receiptPreview} alt="Receipt preview" />
                            <button
                              type="button"
                              className="change-receipt-btn"
                              onClick={() => document.getElementById('receipt-upload').click()}
                            >
                              ფოტოს შეცვლა
                            </button>
                          </div>
                        ) : (
                          <label htmlFor="receipt-upload" className="receipt-upload-label">
                            <div className="upload-icon">📤</div>
                            <p>ატვირთეთ ქვითარი</p>
                            <small>JPG, PNG ან WebP (მაქს 5მბ)</small>
                          </label>
                        )}
                      </div>
                      
                      <Button
                        type="button"
                        variant="primary"
                        block
                        onClick={handleManualTransferSubmit}
                        loading={uploadingReceipt}
                        disabled={!receiptFile}
                        style={{ marginTop: '1rem' }}
                      >
                        {uploadingReceipt ? 'იტვირთება...' : 'ატვირთეთ და დაასრულეთ შეკვეთა'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* TBC Bank Transfer Details */}
              {formData.paymentMethod === 'tbc_transfer' && paymentMethods.tbc_transfer?.enabled && (
                <div className="bank-transfer-section">
                  <div className="bank-details-card">
                    <h3 className="bank-transfer-title">🏦 TBC ბანკი</h3>
                    
                    <div className="bank-detail-row">
                      <span>ანგარიში (IBAN):</span>
                      <strong>{paymentMethods.tbc_transfer.settings.tbc_iban || paymentMethods.tbc_transfer.settings.iban || 'GE00TB0000000000000000'}</strong>
                    </div>
                    <div className="bank-detail-row">
                      <span>თანხა:</span>
                      <strong>{total.toFixed(2)} ₾</strong>
                    </div>
                    <div className="bank-detail-row">
                      <span>მიმღები:</span>
                      <strong>{paymentMethods.tbc_transfer.settings.account_holder || 'CLOTH Store'}</strong>
                    </div>
                    <div className="bank-detail-row">
                      <span>დანიშნულება:</span>
                      <strong>Order Payment</strong>
                    </div>
                    
                    <div className="receipt-upload-section">
                      <h4 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
                        📸 ატვირთეთ ქვითარი
                      </h4>
                      <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
                        გადახდის შემდეგ ატვირთეთ თქვენი გადახდის ქვითარი, რათა დავადასტუროთ თქვენი შეკვეთა.
                      </p>
                      
                      <div className="receipt-upload-container">
                        <input
                          type="file"
                          id="receipt-upload-tbc"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={handleReceiptChange}
                          style={{ display: 'none' }}
                        />
                        
                        {receiptPreview ? (
                          <div className="receipt-preview">
                            <img src={receiptPreview} alt="Receipt preview" />
                            <button
                              type="button"
                              className="change-receipt-btn"
                              onClick={() => document.getElementById('receipt-upload-tbc').click()}
                            >
                              ფოტოს შეცვლა
                            </button>
                          </div>
                        ) : (
                          <label htmlFor="receipt-upload-tbc" className="receipt-upload-label">
                            <div className="upload-icon">📤</div>
                            <p>ატვირთეთ ქვითარი</p>
                            <small>JPG, PNG ან WebP (მაქს 5მბ)</small>
                          </label>
                        )}
                      </div>
                      
                      <Button
                        type="button"
                        variant="primary"
                        block
                        onClick={handleManualTransferSubmit}
                        loading={uploadingReceipt}
                        disabled={!receiptFile}
                        style={{ marginTop: '1rem' }}
                      >
                        {uploadingReceipt ? 'იტვირთება...' : 'ატვირთეთ და დაასრულეთ შეკვეთა'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="checkout-error">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            {/* Submit Button - Only show for BOG iPay */}
            {formData.paymentMethod === 'bog_ipay' && (
              <>
                <Button 
                  type="submit" 
                  variant="primary" 
                  block 
                  loading={loading}
                  className="checkout-submit-btn"
                >
                  {loading ? `${t('checkout.wait')}...` : `${t('checkout.pay')} ${total.toFixed(2)} ₾`}
                </Button>
                <p className="payment-redirect-note">
                  🔒 You will be redirected to BOG iPay secure payment page
                </p>
              </>
            )}
          </form>

          {/* Order Summary Sidebar */}
          <div className="checkout-sidebar">
            <div className="order-summary">
              <h2 className="summary-title">{t('checkout.orderSummary')}</h2>
              
              <div className="summary-items">
                {cart.map((item) => (
                  <div key={item.id} className="summary-item">
                    <div className="summary-item-image">
                      {item.primary_image || item.image_url ? (
                        <img
                          src={getImageUrl(item.primary_image || item.image_url)}
                          alt={item.name}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="no-image">📦</div>';
                          }}
                        />
                      ) : (
                        <div className="no-image">📦</div>
                      )}
                    </div>
                    <div className="summary-item-info">
                      <h4>{item.name}</h4>
                      <p className="summary-item-qty">
                        {t('cart.quantity')}: {item.quantity} × {parseFloat(item.price).toFixed(2)} ₾
                      </p>
                    </div>
                    <div className="summary-item-price">
                      {(item.price * item.quantity).toFixed(2)} ₾
                    </div>
                  </div>
                ))}
              </div>

              <div className="summary-divider"></div>

              <div className="summary-totals">
                <div className="summary-row">
                  <span>{t('cart.subtotal')}</span>
                  <span>{subtotal.toFixed(2)} ₾</span>
                </div>

                <div className="summary-divider"></div>

                <div className="summary-row summary-total">
                  <span>{t('cart.total')}</span>
                  <span>{total.toFixed(2)} ₾</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      <ConfirmModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={modal.onConfirm}
        type={modal.type}
        title={modal.title}
        message={modal.message}
      />
    </div>
  );
};

export default Checkout;